import { useQuery } from '@tanstack/react-query';

import { getGuestTrialStatus } from '@/api/ai/client';
import { useUser } from '@/lib/auth';

export const TRIAL_STATUS_QUERY_KEY = ['/quiz-builder/trial-status'] as const;

/**
 * Quota status query hook
 *
 * 비로그인 유저와 로그인-FREE 유저에게 동일한 쿼터 제한 정책 적용.
 * BASIC/PRO 유저는 쿼터 제한 없음 (API 호출 없음).
 *
 * @returns {Object} Query result with quota information
 * @property {boolean} isQuotaLimited - 쿼터 제한 대상 여부 (guest 또는 FREE plan)
 * @property {boolean} isQuotaExhausted - 쿼터 소진 여부
 */
export const useAiTrialQuotaQuery = () => {
  const { user } = useUser();
  const isQuotaLimited = !user || user.planType === 'FREE';

  const { data, isLoading, isError } = useQuery({
    queryKey: TRIAL_STATUS_QUERY_KEY,
    queryFn: getGuestTrialStatus,
    enabled: isQuotaLimited,
    gcTime: 0,
    staleTime: 60_000, // 1분 — 쿼터는 생성 후에만 변경됨
    retry: false,
    throwOnError: false,
  });

  // Strict 정책: API 에러(data === undefined)일 때도 쿼터 소진으로 처리
  const isQuotaExhausted = data === undefined ? isError : data.remaining === 0;

  return {
    data,
    isLoading,
    isError,
    isQuotaLimited,
    isQuotaExhausted,
  };
};
